import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import AspectRatio from '@/components/AspectRatio';
import { getAdaptedBaseCardContent } from '@/modules/content/card/utils/getAdaptedBaseCardContent';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import { BaseCardImageTransitionType } from '@/modules/content/components/ContentCard/BaseCard/types';
import {
  DISPLAY_CARD_IMAGE_CONTAINER_STYLES,
  DISPLAY_CARD_IMAGE_CONTAINER_STYLES_FOR_DESKTOP,
  DISPLAY_CARD_IMAGE_HEIGHT_MOBILE,
  DISPLAY_CARD_IMAGE_HEIGHT_WEB,
  displayCardImageHeight,
} from '@/modules/content/components/ContentCard/DisplayCard/styles';
import { ContentCardTypeEnum } from '@/modules/content/config/cards';

import { ContentOverdueDueEnum } from '../utils/types';
import { renderProgressStatus } from './renderers';
import { DisplayCardProps } from './types';

const MOBILE_ASPECT_RATIO = 9 / 7;
const DESKTOP_ASPECT_RATIO = 1;

const renderStatusPill = (isMobile: boolean, isTodosCard: boolean) => {
  if (isTodosCard) {
    return null;
  }
  return (
    <BaseCard.Status className={cn('absolute', isMobile ? 'top-10 start-8' : 'top-16 start-16')} />
  );
};

const DisplayCard: React.FC<DisplayCardProps> = (props) => {
  const {
    content: contentProp,
    baseContent,
    footer: footerProp,
    className,
    accessories,
    imageProps,
    isNewHomepage,
    disabled,
    isTodosCard = false,
    isDiscoverPage = false,
    noDescription = false,
  } = props;

  const { t } = useTranslation();
  const content = baseContent ?? getAdaptedBaseCardContent(t, contentProp);
  const isCompleted = content?.status?.label?.toUpperCase() == ContentOverdueDueEnum.Completed;
  const footer = footerProp ?? renderProgressStatus(t, content.progress, isDiscoverPage);

  const { isMobile } = useScreen();

  const aspectRatio = isMobile ? MOBILE_ASPECT_RATIO : DESKTOP_ASPECT_RATIO;

  const getHeight = () => {
    if (isMobile) {
      return DISPLAY_CARD_IMAGE_HEIGHT_MOBILE * 10;
    }
    return DISPLAY_CARD_IMAGE_HEIGHT_WEB * 10 + (noDescription ? 26 : 0);
  };

  const height = isDiscoverPage ? imageProps.width / aspectRatio : getHeight(); // rem to px

  return (
    <BaseCard.Provider
      cardType={ContentCardTypeEnum.Display}
      className={cn(className, 'group relative h-full border border-border rounded-16 px-12 py-12')}
      content={content}
      direction="col"
      disabled={disabled}>
      {isDiscoverPage ? (
        <AspectRatio
          aspectRatio={aspectRatio}
          className={cn(
            DISPLAY_CARD_IMAGE_CONTAINER_STYLES,
            'w-full',
            !isMobile ? DISPLAY_CARD_IMAGE_CONTAINER_STYLES_FOR_DESKTOP : undefined,
          )}>
          <BaseCard.Image
            alt="content image"
            className="h-full w-full"
            containerClassName="rounded-16 xs:rounded h-full"
            height={height}
            transitionType={BaseCardImageTransitionType.Hover}
            width={imageProps.width}>
            {!props.disabled ? <BaseCard.Bookmark /> : null}
            {accessories}
          </BaseCard.Image>
        </AspectRatio>
      ) : (
        <BaseCard.Image
          alt="content image"
          className={cn(`w-full`, displayCardImageHeight(isMobile))}
          containerClassName={cn(
            'rounded-16 xs:rounded w-full',
            DISPLAY_CARD_IMAGE_CONTAINER_STYLES,
            !isMobile ? DISPLAY_CARD_IMAGE_CONTAINER_STYLES_FOR_DESKTOP : undefined,
          )}
          height={height}
          transitionType={BaseCardImageTransitionType.Hover}
          width={imageProps.width}>
          {!props.disabled ? <BaseCard.Bookmark /> : null}
          {accessories}
        </BaseCard.Image>
      )}
      <Stack className="mt-16 px-8 xs:px-4 flex-grow" direction="col">
        {<BaseCard.Label className="truncate" />}
        <BaseCard.Name
          className="mt-4 text-text-primary card-name"
          numberOfLines={1}
          variant={isNewHomepage ? (isMobile ? 'label-01' : 'label-02') : undefined}
        />
        {footer}
      </Stack>
      {isCompleted ? null : renderStatusPill(isMobile, isTodosCard)}
    </BaseCard.Provider>
  );
};

export default DisplayCard;
